import type { SystemAdminRole } from "@price-rescue/api-contracts";

import { ADMIN_ROUTES, type AdminRouteDefinition } from "./routes.js";

export type AdminRole = SystemAdminRole;

export interface AdminSessionView {
  readonly adminUserId: string;
  readonly email: string;
  readonly role: AdminRole;
  readonly expiresAt: string;
  readonly permissions: readonly string[];
}

export interface AdminRouteAccessDecision {
  readonly allowed: boolean;
  readonly route: AdminRouteDefinition;
  readonly reason: "ALLOWED" | "LOGIN_REQUIRED" | "PERMISSION_DENIED";
  readonly banner: string | null;
}

export function decideAdminRouteAccess(
  routePath: string,
  session: AdminSessionView | null,
): AdminRouteAccessDecision {
  const route = findRoute(routePath);

  if (session === null) {
    return denied(route, "LOGIN_REQUIRED", "Sign in with system admin Cognito to continue.");
  }

  if (!session.permissions.includes(route.permission)) {
    return denied(route, "PERMISSION_DENIED", "Your admin role does not allow this operation.");
  }

  return {
    allowed: true,
    route,
    reason: "ALLOWED",
    banner: route.supportAccessBanner === true ? supportAccessBanner(session) : null,
  };
}

export function visibleAdminRoutes(
  session: AdminSessionView | null,
): readonly AdminRouteDefinition[] {
  if (session === null) {
    return [];
  }

  return ADMIN_ROUTES.filter((route) => session.permissions.includes(route.permission));
}

function findRoute(routePath: string): AdminRouteDefinition {
  const normalizedRoutePath = normalizeViewRoutePath(routePath);
  return (
    [...ADMIN_ROUTES]
      .sort((left, right) => right.path.length - left.path.length)
      .find(
        (route) =>
          route.path === normalizedRoutePath ||
          normalizedRoutePath.startsWith(route.path.replace(":storeHash", "")),
      ) ?? ADMIN_ROUTES[0]!
  );
}

function normalizeViewRoutePath(routePath: string): string {
  if (routePath.includes("support-feedback")) return "/admin/feedback";
  if (routePath.includes("installation-dashboard")) return "/admin";
  if (routePath.includes("installations-list") || routePath.includes("uninstalled-stores"))
    return "/admin/stores";
  if (
    routePath.includes("store-detail") ||
    routePath.includes("support-session") ||
    routePath.includes("suspend-store") ||
    routePath.includes("resume-store")
  )
    return "/admin/stores/:storeHash";
  if (routePath.includes("bedrock-prompt-diagnostics")) return "/admin/health";
  if (routePath.includes("extraction-diagnostics")) return "/admin/health";
  if (routePath.includes("data-browser")) return "/admin/data-browser";
  if (isDomainRoute(routePath)) return "/admin/health";
  if (
    routePath.includes("jobs") ||
    routePath.includes("job-") ||
    routePath.includes("retry-job") ||
    routePath.includes("bulk-replay") ||
    routePath.includes("quarantine-job")
  )
    return "/admin/jobs/failed";
  if (routePath.includes("usage")) return "/admin/usage";
  if (routePath.includes("audit") || routePath.includes("note")) return "/admin/audit";
  if (isOperationsRoute(routePath)) return "/admin";
  return routePath;
}

function isDomainRoute(routePath: string): boolean {
  return [
    "domain-health",
    "domain-status",
    "domain-diagnostic",
    "change-domain",
    "run-domain",
  ].some((fragment) => routePath.includes(fragment));
}

function isOperationsRoute(routePath: string): boolean {
  return [
    "incident",
    "kill-switch",
    "degradation",
    "release",
    "protected-data",
    "admin-settings",
  ].some((fragment) => routePath.includes(fragment));
}

function denied(
  route: AdminRouteDefinition,
  reason: "LOGIN_REQUIRED" | "PERMISSION_DENIED",
  banner: string,
): AdminRouteAccessDecision {
  return {
    allowed: false,
    route,
    reason,
    banner,
  };
}

function supportAccessBanner(session: AdminSessionView): string {
  if (session.role === "SUPPORT_ADMIN") {
    return "Store-specific support actions require an active reason-coded support access window.";
  }

  return "Internal admin view: never copy customer data, credentials, or raw provider payloads into notes.";
}
